import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "@/hooks/useToast";
import type { EventUpdateRequest } from "@/lib/humhub-api/events";
import { eventsApi } from "@/lib/humhub-api/events";
import { queryInvalidationHelpers } from "@/lib/queryInvalidationHelpers";

export const useUpdateEvent = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const mutation = useMutation({
    mutationFn: async ({
      id,
      event,
    }: {
      id: number;
      event: EventUpdateRequest;
    }) => {
      return eventsApi.updateEvent(id, event);
    },
    onSuccess: (updatedEvent, variables) => {
      // イベント関連クエリを無効化してリフレッシュ
      queryInvalidationHelpers.invalidateEventQueries(queryClient);

      // 繰り返しスコープ更新の場合は全イベント詳細を無効化
      if (variables.event.scope && variables.event.scope !== "single") {
        queryClient.invalidateQueries({
          predicate: (query) => query.queryKey[0] === "eventDetail",
        });
      } else {
        // 特定のイベント詳細も無効化
        queryClient.invalidateQueries({
          queryKey: ["eventDetail", variables.id],
        });
      }

      if (updatedEvent?.code) {
        queryClient.invalidateQueries({
          queryKey: ["linkPreview"],
          predicate: (query) => {
            const meta = query.meta as { eventCode?: string } | undefined;
            return meta?.eventCode === updatedEvent.code;
          },
        });
      }

      toast({
        title: t("common.success"),
        description: t("eventManage.eventUpdated"),
      });
    },
    onError: (error) => {
      console.error("Error updating event:", error);
      toast({
        title: t("common.error"),
        description: t("eventManage.eventUpdateFailed"),
        variant: "error",
      });
    },
  });

  const updateEvent = useCallback(
    async (id: number, event: EventUpdateRequest) => {
      return mutation.mutateAsync({ id, event });
    },
    [mutation],
  );

  const reset = useCallback(() => {
    mutation.reset();
  }, [mutation]);

  return {
    updateEvent,
    updatedData: mutation.data || null,
    isLoading: mutation.isPending,
    error: mutation.error ? String(mutation.error) : null,
    isSuccess: mutation.isSuccess,
    reset,
  };
};
